import { memo, useRef, useCallback } from 'react';
import filesize from 'filesize';
import DropzoneComponent from 'react-dropzone';
import { notify } from '@/components/toast';

import { uploadConfig } from 'client/media';

import { Button } from '@/shadcn/button';
import { CloudUpload, MousePointer2 } from 'lucide-react';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

type Props = {
  onDropZoneEnter: () => void;
  onDropZoneLeave: () => void;
  onDrop: () => void;
  dropZoneActive: boolean;
  title?: string;
  buttonLabel?: string;
  error?: any;
  maxFileSize?: number;
  className?: string;
};

export const DropZone = memo(
  ({
    className,
    onDropZoneEnter,
    onDropZoneLeave,
    onDrop,
    dropZoneActive,
    title = 'Drag a file here',
    buttonLabel = 'select a file from your device',
    error = null,
    maxFileSize = uploadConfig.MAX_FILE_SIZE
  }: Props) => {
    const dropZoneRef = useRef<DropzoneComponent>(null);

    const onSelectFileClick = useCallback(() => {
      dropZoneRef.current?.open();
    }, [dropZoneRef]);

    const handleOnDropRejected = file => {
      const { name, size } = file[0];

      notify.error(
        `The size limit is ${filesize(maxFileSize)}, your ${name} is ${filesize(
          size
        )}`
      );
    };

    const { MAX_FILE_SIZE } = uploadConfig;

    return (
      // @ts-expect-error DropzoneComponent is incorrectly typed
      <DropzoneComponent
        className={cn(
          'bg-card/60 border-input rounded-lg border p-1 backdrop-blur-md',
          className
        )}
        onDragEnter={onDropZoneEnter}
        onDragLeave={onDropZoneLeave}
        onDrop={onDrop}
        maxSize={MAX_FILE_SIZE}
        onDropRejected={handleOnDropRejected}
        data-drag-enter={dropZoneActive}
        data-cy="dropzone"
        disableClick
        ref={dropZoneRef}
      >
        <div className="border-input bg-card text-card-foreground flex size-full flex-col items-center gap-3 rounded-sm border border-dashed p-4 py-6">
          <CloudUpload size={35} className="text-card-foreground/50 my-4" />
          <div className="text-card-foreground/80 text-sm">{title}</div>
          <span className="text-muted-foreground text-[10px] font-semibold">
            OR
          </span>
          <Button
            data-cy="select-file-from-device"
            type="button"
            onClick={onSelectFileClick}
            aria-label="Select file from device"
          >
            <MousePointer2 />
            {buttonLabel}
          </Button>
          {error && (
            <div className="text-destructive mt-5 flex flex-row flex-nowrap items-center text-xs">
              <div className="mr-1 font-semibold">{error.fileName}</div>
              <div>{error.message}</div>
              {error.additionalInfo && (
                <div className="ml-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <div className="text-destructive-foreground bg-destructive cursor-default rounded-full px-1 text-[9px] font-bold">
                          ?
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {error.additionalInfo}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          )}
        </div>
      </DropzoneComponent>
    );
  }
);
